// src/lib/pricing.js
// Dynamic pricing library - fetches live pricing from backend API
// Falls back to hardcoded constants if API fails

// Fallback constants (used if API fails)
const FALLBACK_MEAL_PLAN_PRICING = {
  "3 Meals a Day": {
    "5 Days Veg": { Weekly: 125, Monthly: 380 },
    "5 Days Non-Veg|1 NV/day": { Weekly: 150, Monthly: 550 },
    "5 Days Non-Veg|2 NV/day": { Weekly: 160, Monthly: 570 },
    "2 Days Non-Veg, 3 Days Veg|1 NV/day": { Weekly: 130, Monthly: 475 },
    "2 Days Non-Veg, 3 Days Veg|2 NV/day": { Weekly: 140, Monthly: 495 },
  },
  "2 Meals a Day": {
    "5 Days Veg": { Weekly: 85, Monthly: 290 },
    "5 Days Non-Veg|1 NV + 1 Veg": { Weekly: 120, Monthly: 365 },
    "5 Days Non-Veg|Both NV": { Weekly: 130, Monthly: 385 },
    "2 Days Non-Veg, 3 Days Veg|1 NV + 1 Veg": { Weekly: 100, Monthly: 315 },
    "2 Days Non-Veg, 3 Days Veg|Both NV": { Weekly: 110, Monthly: 335 },
  }
};

const FALLBACK_TRUCKERS_PRICING = {
  "Truckers 10 Meals a Week": {
    "5 Veg + 5 Non-Veg": 120,
    "10 Veg Meals": 100,
    "10 Non-Veg Meals": 130,
  },
  "Truckers 12 Meals a Week": {
    "6 Veg + 6 Non-Veg": 140,
    "12 Veg Meals": 120,
  }
};

const FALLBACK_ADD_ONS_PRICING = {
  "5 Eggs Daily": { Weekly: 15, Monthly: 50 },
  "1 Veg Meal Daily": { Weekly: 15, Monthly: 50 },
  "1 Non Veg Meal Daily": { Weekly: 25, Monthly: 85 },
  "Bulk Me Up": { Weekly: 30, Monthly: 100 },
  "Protein Balls (2)": { Weekly: 45, Monthly: 160 },
  "Fruit on the Side": { Weekly: 50, Monthly: 180 },
};

// Cache for live pricing data
let cachedPricing = null;
let cacheTimestamp = null;

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch current active pricing from backend API
 * Returns cached data if available and fresh (< 5 minutes old)
 * Falls back to hardcoded constants if API call fails
 */
export async function fetchLivePricing() {
  // Return cached data if fresh
  if (cachedPricing && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_TTL)) {
    return cachedPricing;
  }

  try {
    const { http } = await import('./http');
    const response = await http.get('/payment/pricing');
    const data = response.data?.data;

    if (data?.plans && data?.truckers && data?.addons) {
      cachedPricing = {
        plans: data.plans,
        truckers: data.truckers,
        addons: data.addons,
      };
      cacheTimestamp = Date.now();
      return cachedPricing;
    }
  } catch (err) {
    console.warn('Failed to fetch live pricing, using fallback:', err.message);
  }

  // Fallback to hardcoded values only on API failure
  return {
    plans: FALLBACK_MEAL_PLAN_PRICING,
    truckers: FALLBACK_TRUCKERS_PRICING,
    addons: FALLBACK_ADD_ONS_PRICING,
  };
}

/**
 * Clear pricing cache (call this when owner updates pricing)
 */
export function clearPricingCache() {
  cachedPricing = null;
  cacheTimestamp = null;
  console.log('🔄 Pricing cache cleared');
}

/**
 * Get current pricing synchronously (uses cache or fallback)
 */
function getCurrentPricing() {
  if (cachedPricing) {
    return cachedPricing;
  }

  // Return fallback if no cached data
  return {
    plans: FALLBACK_MEAL_PLAN_PRICING,
    truckers: FALLBACK_TRUCKERS_PRICING,
    addons: FALLBACK_ADD_ONS_PRICING
  };
}

/**
 * Look up meal plan price
 * Uses live pricing from cache, or fallback if unavailable
 */
export function lookupPlanPrice(planType, planTerm, nvPref, billingTerm) {
  if (!planType || !planTerm) return 0;
  if (planType.startsWith("Truckers")) return 0; // truckers handled separately

  const pricing = getCurrentPricing();
  const withPref = nvPref && nvPref.trim() && nvPref !== "-" ? `${planTerm}|${nvPref.trim()}` : planTerm;
  const tier = pricing.plans[planType]?.[withPref];
  return tier?.[billingTerm] ?? 0;
}

/**
 * Look up truckers plan price
 * Uses live pricing from cache, or fallback if unavailable
 */
export function lookupTruckersPrice(truckersType, truckersTerm) {
  const pricing = getCurrentPricing();
  return pricing.truckers[truckersType]?.[truckersTerm] ?? 0;
}

/**
 * Sum add-ons prices
 * Uses live pricing from cache, or fallback if unavailable
 */
export function sumAddOnsPrice(addOns = [], billingTerm = "Weekly") {
  const pricing = getCurrentPricing();
  return addOns.reduce((acc, name) => acc + (pricing.addons[name]?.[billingTerm] ?? 0), 0);
}

/**
 * Get all available meal plans with pricing
 */
export function getAllMealPlans() {
  const pricing = getCurrentPricing();
  return pricing.plans;
}

/**
 * Get all available trucker plans with pricing
 */
export function getAllTruckerPlans() {
  const pricing = getCurrentPricing();
  return pricing.truckers;
}

/**
 * Get all available add-ons with pricing
 */
export function getAllAddOns() {
  const pricing = getCurrentPricing();
  return pricing.addons;
}

// HST helpers — all backend prices include 13% HST
const HST_RATE = 0.13;

export function getBasePrice(hstInclusive) {
  const amount = parseFloat(hstInclusive) || 0;
  return (Math.round((amount / (1 + HST_RATE)) * 100) / 100).toFixed(2);
}

export function getHSTAmount(hstInclusive) {
  const amount = parseFloat(hstInclusive) || 0;
  const base = Math.round((amount / (1 + HST_RATE)) * 100) / 100;
  return (amount - base).toFixed(2);
}

export function formatPrice(hstInclusive) {
  const amount = parseFloat(hstInclusive) || 0;
  return amount.toFixed(2);
}
